import * as React from "react";
import type { DateRange } from "react-day-picker";
import { CalendarDays, Check } from "lucide-react";

import { Button } from "../../primitives/button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import styles from "./date-range-picker.module.css";

export type DateRangeValue = DateRange | undefined;
export type DateRangePreset = {
  value: string;
  label: string;
};

function formatRange(value: DateRangeValue): string {
  if (!value?.from) return "Custom range";
  const from = value.from.toLocaleDateString("en-GB", { dateStyle: "medium" });
  if (!value.to) return `${from} – …`;
  return `${from} – ${value.to.toLocaleDateString("en-GB", { dateStyle: "medium" })}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function clampMonth(month: Date, startMonth: Date, endMonth: Date): Date {
  if (month < startMonth) return startMonth;
  if (month > endMonth) return endMonth;
  return month;
}

export function DateRangePicker({
  value,
  onChange,
  label = "Choose date range",
  valueLabel,
  presets,
  selectedPreset,
  onPresetChange,
  minDate,
  maxDate,
}: {
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  label?: string;
  valueLabel?: string;
  presets?: readonly DateRangePreset[];
  selectedPreset?: string;
  onPresetChange?: (value: string) => void;
  minDate?: Date;
  maxDate?: Date;
}) {
  const [open, setOpen] = React.useState(false);
  const [draftValue, setDraftValue] = React.useState<DateRangeValue>(value);
  const today = React.useMemo(() => new Date(), []);
  const minDateTime = minDate?.getTime();
  const maxDateTime = maxDate?.getTime();
  const valueFromTime = value?.from?.getTime();
  const startMonth = React.useMemo(
    () =>
      startOfMonth(
        minDateTime === undefined
          ? new Date(today.getFullYear() - 100, 0, 1)
          : new Date(minDateTime),
      ),
    [minDateTime, today],
  );
  const endMonth = React.useMemo(
    () =>
      startOfMonth(
        maxDateTime === undefined
          ? new Date(
              Math.max(
                today.getFullYear(),
                valueFromTime === undefined
                  ? 0
                  : new Date(valueFromTime).getFullYear(),
              ),
              11,
              1,
            )
          : new Date(maxDateTime),
      ),
    [maxDateTime, today, valueFromTime],
  );
  const valueMonth = React.useMemo(
    () =>
      clampMonth(
        startOfMonth(
          valueFromTime === undefined ? today : new Date(valueFromTime),
        ),
        startMonth,
        endMonth,
      ),
    [valueFromTime, startMonth, endMonth, today],
  );
  const [displayMonth, setDisplayMonth] = React.useState(valueMonth);

  React.useEffect(() => {
    if (!open) {
      setDraftValue(value);
      setDisplayMonth(valueMonth);
    }
  }, [open, value, valueMonth]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftValue(value);
      setDisplayMonth(valueMonth);
    }
  };

  const handleCancel = () => {
    setDraftValue(value);
    setOpen(false);
  };

  const handleApply = () => {
    if (!draftValue?.from) return;
    onChange?.({ from: draftValue.from, to: draftValue.to ?? draftValue.from });
    setOpen(false);
  };

  const handleSelect = (nextValue: DateRangeValue) => {
    setDraftValue(nextValue);
    setOpen(true);
  };

  const handlePresetChange = (preset: DateRangePreset) => {
    onPresetChange?.(preset.value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={label}
          className={styles.trigger}
        >
          <span>{valueLabel ?? formatRange(value)}</span>
          <CalendarDays aria-hidden="true" className={styles.triggerIcon} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={styles.content}>
        {presets && presets.length > 0 && (
          <div className={styles.presetSection} aria-label="Date range presets">
            <p className={styles.presetLabel}>Presets</p>
            <div className={styles.presets}>
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={styles.preset}
                  aria-pressed={selectedPreset === preset.value}
                  onClick={() => handlePresetChange(preset)}
                >
                  <span>{preset.label}</span>
                  {selectedPreset === preset.value && (
                    <Check aria-hidden="true" className={styles.presetIcon} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        <Calendar
          className={styles.calendar}
          classNames={{
            root: styles.calendarRoot,
            dropdowns: styles.dropdowns,
            dropdown_root: styles.dropdownRoot,
            dropdown: styles.dropdown,
            caption_label: styles.captionLabel,
            month_caption: styles.monthCaption,
            weekday: styles.weekday,
            day: styles.day,
            today: styles.today,
            outside: styles.outside,
            disabled: styles.disabled,
            range_start: styles.rangeStart,
            range_middle: styles.rangeMiddle,
            range_end: styles.rangeEnd,
            day_button: styles.dayButton,
            button_previous: styles.navButton,
            button_next: styles.navButton,
          }}
          mode="range"
          captionLayout="dropdown"
          navLayout="after"
          selected={draftValue}
          min={0}
          disabled={[
            ...(minDate ? [{ before: minDate }] : []),
            ...(maxDate ? [{ after: maxDate }] : []),
          ]}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          startMonth={startMonth}
          endMonth={endMonth}
          numberOfMonths={2}
          onSelect={handleSelect}
        />
        <div className={styles.actions}>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={styles.cancelButton}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className={styles.applyButton}
            onClick={handleApply}
            disabled={!draftValue?.from}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
